import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Anchor } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 p-4">
      <Card className="max-w-3xl mx-auto bg-white/95 backdrop-blur">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <Anchor className="w-8 h-8 text-cyan-600" />
            <h1 className="text-2xl font-bold text-gray-800">Boat 360 Privacy Policy</h1>
          </div>
          
          <p className="text-sm text-gray-500 mb-6">Last Updated: January 2025</p>
          
          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">1. Introduction</h2>
              <p>
                Boat 360 ("we," "our," or "us") is operated by SafetyMind AI. This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application and services.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">2. Information We Collect</h2>
              
              <h3 className="font-medium mt-3 mb-1">Account Information</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Email address</li>
                <li>Display name</li>
                <li>Profile photo (optional)</li>
              </ul>

              <h3 className="font-medium mt-3 mb-1">Location Data</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>GPS coordinates (when you grant permission)</li>
                <li>Speed and heading while boating</li>
                <li>Trip history and routes</li>
              </ul>

              <h3 className="font-medium mt-3 mb-1">User Content</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Float plans and trip information</li>
                <li>Boat information</li>
                <li>Hazard reports</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">3. How We Use Your Information</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Display your position on the map</li>
                <li>Share your location with friends/family you approve</li>
                <li>Provide weather information for your location</li>
                <li>Enable SOS and safety features</li>
                <li>Create and share float plans</li>
                <li>Show nearby marinas and services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">4. Location Data</h2>
              <p>
                Location tracking is essential for Boat 360's core features. You control when we access your location:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>Foreground:</strong> Location is tracked when the app is open</li>
                <li><strong>Background (Premium):</strong> Location is tracked during active trips to share with your crew</li>
              </ul>
              <p className="mt-2">
                You can disable location access at any time in your device settings.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">5. Sharing Your Information</h2>
              <p>We share your information only:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>With friends you explicitly add and approve</li>
                <li>When you trigger an SOS alert (shared with nearby boaters)</li>
                <li>When you share a float plan or trip link</li>
              </ul>
              <p className="mt-2">
                We do NOT sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">6. Data Security</h2>
              <p>
                We use industry-standard encryption (HTTPS/TLS) to protect your data in transit. Your information is stored securely on protected servers.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">7. Your Rights</h2>
              <p>You can:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Access and update your account information</li>
                <li>Delete your account and associated data</li>
                <li>Disable location tracking at any time</li>
                <li>Remove friends from your crew list</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">8. Children's Privacy</h2>
              <p>
                Boat 360 is not intended for children under 13. We do not knowingly collect information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes through the app or by email.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">10. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, contact us at:
              </p>
              <p className="mt-2">
                <strong>SafetyMind AI</strong><br />
                Email: support@safetymindai.com
              </p>
            </section>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyPolicy;
